import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/site/ui";

import PublicLayout from "@/components/site/PublicLayout";
import Home from "@/pages/Home";
import Experiences from "@/pages/Experiences";
import ExperienceDetail from "@/pages/ExperienceDetail";
import Membership from "@/pages/Membership";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Policies from "@/pages/Policies";
import ResortGuide from "@/pages/ResortGuide";
import ManageReservation from "@/pages/ManageReservation";
import Guestbook from "@/pages/Guestbook";
import Book from "@/pages/Book";
import StaticPage from "@/pages/StaticPage";
import InvitationLanding from "@/pages/InvitationLanding";
import GoogleCallback from "@/pages/GoogleCallback";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PendingActivation from "@/pages/PendingActivation";
import ForgotPassword from "@/pages/ForgotPassword";

import PortalLayout from "@/components/portal/PortalLayout";
import PortalHome from "@/pages/portal/PortalHome";
import PortalReservations from "@/pages/portal/PortalReservations";
import PortalReservation from "@/pages/portal/PortalReservation";
import PortalConcierge from "@/pages/portal/PortalConcierge";
import PortalProfile from "@/pages/portal/PortalProfile";
import PortalCard from "@/pages/portal/PortalCard";

import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import RoleDashboard from "@/pages/admin/RoleDashboard";
import AdminRooms from "@/pages/admin/AdminRooms";
import AdminHousekeeping from "@/pages/admin/AdminHousekeeping";
import AdminSchedule from "@/pages/admin/AdminSchedule";
import MySchedule from "@/pages/admin/MySchedule";
import AdminOperationsBoard from "@/pages/admin/AdminOperationsBoard";
import AdminFrontDesk from "@/pages/admin/AdminFrontDesk";
import AdminPermissionsMatrix from "@/pages/admin/AdminPermissionsMatrix";
import AdminReservations from "@/pages/admin/AdminReservations";
import AdminReservationDetail from "@/pages/admin/AdminReservationDetail";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminGuests from "@/pages/admin/AdminGuests";
import AdminExperiences from "@/pages/admin/AdminExperiences";
import AdminInvitations from "@/pages/admin/AdminInvitations";
import AdminMemberships from "@/pages/admin/AdminMemberships";
import AdminConcierge from "@/pages/admin/AdminConcierge";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminEventDetail from "@/pages/admin/AdminEventDetail";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminDisplay from "@/pages/admin/AdminDisplay";
import AdminHouseGuide from "@/pages/admin/AdminHouseGuide";
import WelcomeDisplay from "@/pages/WelcomeDisplay";
import AdminInHouse from "@/pages/admin/AdminInHouse";
import AdminTasks from "@/pages/admin/AdminTasks";
import AdminMaintenance from "@/pages/admin/AdminMaintenance";
import AdminIncidents from "@/pages/admin/AdminIncidents";
import AdminComms from "@/pages/admin/AdminComms";
import AdminGuestbook from "@/pages/admin/AdminGuestbook";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminQR from "@/pages/admin/AdminQR";
import AdminGhost from "@/pages/admin/AdminGhost";
import AdminGhostSettings from "@/pages/admin/AdminGhostSettings";
import AdminAccessRequests from "@/pages/admin/AdminAccessRequests";
import AdminAccessReminders from "@/pages/admin/AdminAccessReminders";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminSystemStatus from "@/pages/admin/AdminSystemStatus";
import PaymentRequestCenter from "@/pages/admin/PaymentRequestCenter";
import AdminStaff from "@/pages/admin/AdminStaff";
import StaffInvite from "@/pages/StaffInvite";
import PortalPostStay from "@/pages/portal/PortalPostStay";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentReturn";
import GhostLauncher from "@/components/ghost/GhostLauncher";
import PwaManager from "@/components/PwaManager";

function Protected({ children, staff }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <div className="min-h-screen bg-ink flex items-center justify-center"><Loading /></div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (staff && !user.is_staff) return <Navigate to="/portal" replace />;
  if (!staff && user.is_staff) return <Navigate to="/admin" replace />;
  if (!staff && !user.is_staff && user.account_status && user.account_status !== "Active")
    return <Navigate to="/pending" replace />;
  return children;
}

const LEGAL = {
  privacy: { title: "Privacy Policy", eyebrow: "Legal", sections: [
    { p: "The D respects your privacy. We collect only the information needed to host your stay — your contact details, reservation preferences, and any information you choose to share." },
    { h: "What we collect", p: "Name, contact information, reservation details, guest preferences, and digital check-in acknowledgments." },
    { h: "How we use it", p: "To manage your reservation, personalize your experience, and communicate with you. We never sell your information." },
    { h: "Payment data", p: "The D does not store full card numbers or CVV codes. Only the last four digits, entered manually by staff, may be retained for reference." },
  ]},
  terms: { title: "Terms of Service", eyebrow: "Legal", sections: [
    { p: "By requesting a reservation at The D you agree to our house rules, policies, and the acknowledgments presented at digital check-in." },
    { h: "Access", p: "The D is invitation-only. Access may be revoked at the host's discretion." },
    { h: "Reservations", p: "All reservations are requests subject to host approval. A confirmation number does not guarantee approval until confirmed." },
  ]},
  "cancellation-policy": { title: "Cancellation Policy", eyebrow: "Policies", sections: [
    { p: "Cancellations made 14 or more days before arrival receive a full deposit refund. Cancellations within 14 days may forfeit the deposit." },
    { h: "How to cancel", p: "Cancel from your guest account, or contact your host directly. Availability is released according to policy once cancelled." },
  ]},
  "payment-policy": { title: "Payment Policy", eyebrow: "Policies", sections: [
    { p: "A deposit secures your reservation. The remaining balance is arranged directly with your host prior to arrival." },
    { h: "Accepted methods", p: "Cash, check, wire transfer, bank transfer, Zelle, card by telephone, or a secure external payment link." },
    { h: "Online payments", p: "The D does not process card payments online at this time. Online payments will be introduced securely in the future." },
  ]},
  accessibility: { title: "Accessibility", eyebrow: "For Everyone", sections: [
    { p: "The D is committed to a welcoming, accessible experience. Our platform is built toward WCAG 2.1 AA standards with keyboard navigation, semantic structure, and reduced-motion support." },
    { h: "Accessibility needs", p: "Let us know your needs during digital check-in or through concierge and we will do our best to accommodate them." },
  ]},
  concierge: { title: "Concierge", eyebrow: "At Your Service", sections: [
    { p: "From extra towels to a rosemary cocktail, our concierge is here for you throughout your stay." },
    { h: "How it works", p: "Sign in to your guest account to submit and track requests. Your host is notified instantly." },
  ]},
  "private-events": { title: "Private Events", eyebrow: "Celebrate at The D", sections: [
    { p: "Birthdays, anniversaries, dinners, and pool parties — The D transforms for your celebration." },
    { h: "Plan your event", p: "Request an invitation or contact your host to begin planning. We'll handle the atmosphere, lighting, and details." },
  ]},
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" theme="dark" richColors />
        <PwaManager />
        <a href="#main-content" className="skip-link">Skip to content</a>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/experiences" element={<Experiences />} />
            <Route path="/experiences/:id" element={<ExperienceDetail />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/request-invitation" element={<Contact mode="invite" />} />
            <Route path="/policies" element={<Policies />} />
            <Route path="/resort-guide" element={<ResortGuide />} />
            <Route path="/manage" element={<ManageReservation />} />
            <Route path="/guestbook" element={<Guestbook />} />
            <Route path="/book" element={<Book />} />
            {Object.entries(LEGAL).map(([slug, cfg]) => (
              <Route key={slug} path={`/${slug}`} element={<StaticPage {...cfg} />} />
            ))}
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pending" element={<PendingActivation />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/staff-invite/:token" element={<StaffInvite />} />
          <Route path="/invite/:code" element={<InvitationLanding />} />
          <Route path="/display/:token" element={<WelcomeDisplay />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          <Route path="/portal" element={<Protected><PortalLayout /></Protected>}>
            <Route index element={<PortalHome />} />
            <Route path="reservations" element={<PortalReservations />} />
            <Route path="reservations/:id" element={<PortalReservation />} />
            <Route path="concierge" element={<PortalConcierge />} />
            <Route path="post-stay" element={<PortalPostStay />} />
            <Route path="card" element={<PortalCard />} />
            <Route path="profile" element={<PortalProfile />} />
          </Route>

          <Route path="/admin" element={<Protected staff><AdminLayout /></Protected>}>
            <Route index element={<RoleDashboard />} />
            <Route path="reservations" element={<AdminReservations />} />
            <Route path="reservations/:id" element={<AdminReservationDetail />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="guests" element={<AdminGuests />} />
            <Route path="experiences" element={<AdminExperiences />} />
            <Route path="invitations" element={<AdminInvitations />} />
            <Route path="memberships" element={<AdminMemberships />} />
            <Route path="concierge" element={<AdminConcierge />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="events/:id" element={<AdminEventDetail />} />
            <Route path="in-house" element={<AdminInHouse />} />
            <Route path="tasks" element={<AdminTasks />} />
            <Route path="maintenance" element={<AdminMaintenance />} />
            <Route path="rooms" element={<AdminRooms />} />
            <Route path="housekeeping" element={<AdminHousekeeping />} />
            <Route path="schedule" element={<AdminSchedule />} />
            <Route path="my-schedule" element={<MySchedule />} />
            <Route path="operations" element={<AdminOperationsBoard />} />
            <Route path="frontdesk" element={<AdminFrontDesk />} />
            <Route path="payments" element={<PaymentRequestCenter />} />
            <Route path="permissions-matrix" element={<AdminPermissionsMatrix />} />
            <Route path="incidents" element={<AdminIncidents />} />
            <Route path="communications" element={<AdminComms />} />
            <Route path="guestbook" element={<AdminGuestbook />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="staff" element={<AdminStaff />} />
            <Route path="access-requests" element={<AdminAccessRequests />} />
            <Route path="access-reminders" element={<AdminAccessReminders />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="qr" element={<AdminQR />} />
            <Route path="house-guide" element={<AdminHouseGuide />} />
            <Route path="gallery" element={<AdminGallery />} />
            <Route path="display" element={<AdminDisplay />} />
            <Route path="ghost" element={<AdminGhost />} />
            <Route path="ghost-settings" element={<AdminGhostSettings />} />
            <Route path="system" element={<AdminSystemStatus />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
